import { LimitNames } from '@gonfalon/billing/types';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableAIRunsCheckoutDisplay } from '@gonfalon/dogfood-flags/dogfood-flags';

import type { Feature, ProductDetails } from '../types';

/** Creates a zero-cost product for free plans (e.g. Developer2025). */
export const freeProduct = (
  limitName: LimitNames,
  freeQuantity: number,
  blockSize: number,
  opts?: Pick<ProductDetails, 'isUnbilled' | 'monthlyBilledOnly' | 'displayOnly' | 'gatedByFlag'>,
): ProductDetails => ({
  limitName,
  ...opts,
  freeQuantity,
  annualCommitDetails: { blockSize, pricePerBlockCents: 0, maxQuantity: freeQuantity },
  monthlyOverageDetails: { blockSize, pricePerBlockCents: 0 },
});

export const AI_RUNS_DESCRIPTION =
  'An AI run is generated when you track an LLM request or run an online eval (LLM-as-a-judge).';

export const aiRunsFeature: Feature = {
  text: '5K AI runs',
  tooltip: {
    text: AI_RUNS_DESCRIPTION,
    ctaTarget: getDocumentationUrl('home/ai-configs'),
  },
  gatedByFlag: enableAIRunsCheckoutDisplay,
};

export const sessionsProduct: ProductDetails = {
  limitName: LimitNames.SESSIONS,
  hasBulkPricing: true,
  freeQuantity: 5000,
  annualCommitDetails: {
    blockSize: 1000,
    pricePerBlockCents: 3500,
    maxQuantity: 300_000,
    bulkPricingTiers: [
      { min: 5_000, max: 25_000, pricePerBlockCents: 3500 },
      { min: 25_000, max: 50_000, pricePerBlockCents: 3000 },
      { min: 50_000, max: 150_000, pricePerBlockCents: 2500 },
      { min: 150_000, max: 300_000, pricePerBlockCents: 2500 },
      { min: 300_000, max: 500_000, pricePerBlockCents: 2500 },
      { min: 500_000, max: 1_000_000, pricePerBlockCents: 1500 },
      { min: 1_000_000, max: 10_000_000, pricePerBlockCents: 1500 },
      { min: 10_000_000, max: Number.MAX_SAFE_INTEGER, pricePerBlockCents: 1500 },
    ],
  },
  monthlyOverageDetails: {
    blockSize: 1000,
    pricePerBlockCents: 350,
  },
};

export const errorsProduct: ProductDetails = {
  limitName: LimitNames.ERRORS,
  hasBulkPricing: true,
  freeQuantity: 5000,
  annualCommitDetails: {
    blockSize: 1000,
    pricePerBlockCents: 300,
    maxQuantity: 300_000,
    bulkPricingTiers: [
      { min: 5_000, max: 100_000, pricePerBlockCents: 300 },
      { min: 100_000, max: Number.MAX_SAFE_INTEGER, pricePerBlockCents: 130 },
    ],
  },
  monthlyOverageDetails: {
    blockSize: 1000,
    pricePerBlockCents: 30,
  },
};

export const tracesProduct: ProductDetails = {
  limitName: LimitNames.TRACES,
  freeQuantity: 10_000_000,
  annualCommitDetails: {
    blockSize: 1_000_000,
    pricePerBlockCents: 1500,
    maxQuantity: 100_000_000,
    bulkPricingTiers: [{ min: 10_000_000, max: Number.MAX_SAFE_INTEGER, pricePerBlockCents: 1500 }],
  },
  monthlyOverageDetails: {
    blockSize: 1_000_000,
    pricePerBlockCents: 150,
  },
};

export const logsProduct: ProductDetails = {
  limitName: LimitNames.LOGS,
  freeQuantity: 10_000_000,
  annualCommitDetails: {
    blockSize: 1_000_000,
    pricePerBlockCents: 1500,
    maxQuantity: 100_000_000,
    bulkPricingTiers: [{ min: 10_000_000, max: Number.MAX_SAFE_INTEGER, pricePerBlockCents: 1500 }],
  },
  monthlyOverageDetails: {
    blockSize: 1_000_000,
    pricePerBlockCents: 150,
  },
};

export const experimentationProduct: ProductDetails = {
  limitName: LimitNames.EXPERIMENTATION_MAU,
  isUnbilled: true,
  freeQuantity: 100_000,
  annualCommitDetails: {
    blockSize: 0,
    pricePerBlockCents: 0,
    maxQuantity: 100_000,
  },
  monthlyOverageDetails: {
    blockSize: 0,
    pricePerBlockCents: 0,
  },
};

export const aiRunsProduct: ProductDetails = {
  limitName: LimitNames.AI_RUNS,
  monthlyBilledOnly: true,
  freeQuantity: 5000,
  annualCommitDetails: {
    blockSize: 1000,
    pricePerBlockCents: 0,
    maxQuantity: 5000,
  },
  monthlyOverageDetails: {
    blockSize: 1000,
    pricePerBlockCents: 500,
  },
};

export const aiRunsProductDisplayOnly: ProductDetails = {
  limitName: LimitNames.AI_RUNS,
  displayOnly: true,
  gatedByFlag: enableAIRunsCheckoutDisplay,
  freeQuantity: 5000,
  annualCommitDetails: {
    blockSize: 1000,
    pricePerBlockCents: 0,
    maxQuantity: 5000,
  },
  monthlyOverageDetails: {
    blockSize: 1000,
    pricePerBlockCents: 500,
  },
};
